import { t as tr } from './i18n';
import type { ItemSpecs, SiteRef } from './types';

/**
 * Necesarul de pe șantier (la Precoro, „purchase request”): șeful de șantier
 * scrie ce materiale îi trebuie, cât și până când. Achizițiile îl preiau și fac
 * din el o cerere de ofertă, fără să rescrie liniile, sau îl resping cu motiv.
 *
 * Omul de pe șantier urmărește necesarul până la livrare, fără să vadă prețuri:
 * trimis → se cer oferte → se comandă → comandat → livrat.
 */

/** Ce s-a hotărât cu necesarul. Etapa de după („comandat”, „livrat”) vine din cerere. */
export type RequisitionStatus = 'open' | 'rfq' | 'rejected' | 'canceled';

export type ReqStage =
  | 'open'
  | 'quoting'
  | 'ordering'
  | 'ordered'
  | 'partial'
  | 'delivered'
  | 'dropped'
  | 'rejected'
  | 'canceled';

/** Faptele din care iese etapa: necesarul și, dacă s-a preluat, cererea și comanda ei. */
export interface ReqStageFacts {
  status: RequisitionStatus;
  rfq: {
    closedAt: string | Date | null;
    canceledAt: string | Date | null;
    chosen: boolean;
    /** comanda din cerere în vigoare (emisă sau la aprobare) */
    order: { status: 'issued' | 'pending'; delivered: boolean; completed: boolean } | null;
  } | null;
}

export function reqStage(r: ReqStageFacts): ReqStage {
  if (r.status === 'rejected') return 'rejected';
  if (r.status === 'canceled') return 'canceled';
  if (r.status === 'open' || !r.rfq) return 'open';
  const { rfq } = r;
  if (rfq.order?.status === 'issued') {
    if (rfq.order.completed) return 'delivered';
    return rfq.order.delivered ? 'partial' : 'ordered';
  }
  if (rfq.canceledAt) return 'dropped';
  if (rfq.order?.status === 'pending' || rfq.chosen) return 'ordering';
  if (rfq.closedAt) return 'dropped';
  return 'quoting';
}

export const REQ_STAGE: Record<ReqStage, { label: string; pill: string; hint: string }> = {
  open: {
    get label() {
      return tr('trimis');
    },
    pill: 'todo',
    get hint() {
      return tr('așteaptă să-l preia achizițiile');
    },
  },
  quoting: {
    get label() {
      return tr('se cer oferte');
    },
    pill: 'wait',
    get hint() {
      return tr('achizițiile cer oferte de la furnizori');
    },
  },
  ordering: {
    get label() {
      return tr('se comandă');
    },
    pill: 'act',
    get hint() {
      return tr('furnizorul e ales, comanda se pregătește');
    },
  },
  ordered: {
    get label() {
      return tr('comandat');
    },
    pill: 'got',
    get hint() {
      return tr('comanda a plecat la furnizor');
    },
  },
  partial: {
    get label() {
      return tr('livrat parțial');
    },
    pill: 'got',
    get hint() {
      return tr('a venit o parte pe șantier');
    },
  },
  delivered: {
    get label() {
      return tr('livrat');
    },
    pill: 'got',
    get hint() {
      return tr('a venit tot pe șantier');
    },
  },
  dropped: {
    get label() {
      return tr('oprit');
    },
    pill: 'off',
    get hint() {
      return tr('cererea de ofertă s-a închis fără comandă');
    },
  },
  rejected: {
    get label() {
      return tr('respins');
    },
    pill: 'late',
    get hint() {
      return tr('achizițiile nu-l preiau');
    },
  },
  canceled: {
    get label() {
      return tr('retras');
    },
    pill: 'off',
    get hint() {
      return tr('retras de cine l-a trimis');
    },
  },
};

/** Contoarele de sus ale listei. */
export const REQ_GROUPS = [
  {
    id: 'open',
    get label() {
      return tr('De preluat');
    },
    stages: ['open'],
  },
  {
    id: 'progress',
    get label() {
      return tr('În lucru');
    },
    stages: ['quoting', 'ordering'],
  },
  {
    id: 'ordered',
    get label() {
      return tr('Comandate');
    },
    stages: ['ordered', 'partial'],
  },
  {
    id: 'delivered',
    get label() {
      return tr('Livrate');
    },
    stages: ['delivered'],
  },
  {
    id: 'closed',
    get label() {
      return tr('Respinse sau retrase');
    },
    stages: ['rejected', 'canceled', 'dropped'],
  },
  {
    id: 'all',
    get label() {
      return tr('Toate');
    },
    stages: [
      'open',
      'quoting',
      'ordering',
      'ordered',
      'partial',
      'delivered',
      'dropped',
      'rejected',
      'canceled',
    ],
  },
] as const satisfies readonly { id: string; label: string; stages: readonly ReqStage[] }[];

export type ReqGroup = (typeof REQ_GROUPS)[number]['id'];

export const isReqGroup = (v: string): v is ReqGroup => REQ_GROUPS.some((g) => g.id === v);

export function reqIn(stage: ReqStage, group: ReqGroup): boolean {
  return (REQ_GROUPS.find((g) => g.id === group)!.stages as readonly ReqStage[]).includes(stage);
}

/** „NC-12”: numărul necesarului în firmă. */
export const reqNumber = (seq: number): string => `NC-${seq}`;

export interface RequisitionLine {
  desc: string;
  qty: number;
  unit: string;
  /** chirie: câte zile (din chestionarul de cofraje); lipsă / null = cumpărare */
  rentDays?: number | null;
  /** înălțimile și grosimea, din chestionar; trec la fel în cererea de ofertă */
  specs?: ItemSpecs | null;
}

export interface Requisition {
  id: string;
  /** „NC-12” */
  number: string;
  title: string;
  /** categoria (Armătură, HVAC…); '' la necesarele de dinainte de câmp */
  category: string;
  site: SiteRef;
  /** 'AAAA-LL-ZZ': când trebuie pe șantier; null = nu s-a spus */
  neededBy: string | null;
  /** pentru achiziții, nu pleacă la furnizori */
  note: string;
  status: RequisitionStatus;
  stage: ReqStage;
  items: RequisitionLine[];
  createdBy: { id: string; name: string } | null;
  /** ISO 8601 */
  createdAt: string;
  /** cine l-a preluat sau respins, și când */
  decidedBy: string | null;
  decidedAt: string | null;
  /** motivul respingerii */
  reason: string;
  /** cererea de ofertă făcută din el */
  rfq: { id: string; title: string } | null;
  /** livrarea promisă pe comandă, 'AAAA-LL-ZZ' */
  deliveryDate: string | null;
}

export interface RequisitionInput {
  siteId: string;
  title: string;
  category: string;
  neededBy: string | null;
  note: string;
  items: RequisitionLine[];
}

/**
 * Cererea de ofertă pornită din unul sau mai multe necesare: liniile lor una
 * după alta, titlul primului, cea mai apropiată dată de pe șantier.
 */
export function rfqDraftFrom(
  reqs: Pick<Requisition, 'number' | 'title' | 'category' | 'neededBy' | 'items' | 'site'>[],
): {
  title: string;
  category: string;
  siteId: string;
  neededBy: string | null;
  items: RequisitionLine[];
} {
  const dates = reqs
    .map((r) => r.neededBy)
    .filter((d): d is string => !!d)
    .sort();
  return {
    title: reqs.length === 1 ? reqs[0]!.title : tr('{0} (+{1} necesare)', reqs[0]!.title, reqs.length - 1),
    // prima categorie scrisă; necesarele vechi n-au
    category: reqs.find((r) => r.category)?.category ?? '',
    siteId: reqs[0]!.site.id,
    neededBy: dates[0] ?? null,
    items: reqs.flatMap((r) => r.items),
  };
}

export const SITE_ROLE_MESSAGE =
  'Contul de șantier trimite necesar de materiale și trece livrările. Restul îl fac achizițiile.';
